import React from 'react';
import PropTypes from 'prop-types';

/**
 * FormField — Label + Feld + Hinweis/Fehlertext, konsistent verdrahtet.
 *
 * Erwartet genau ein Kind (Input/Textarea/Select aus ui/Input.jsx) und reicht
 * ihm automatisch id, aria-describedby und error weiter — Aufrufer müssen keine
 * IDs mehr von Hand synchron halten.
 *
 * <FormField label="Email" error={errors.email} required>
 *   <Input type="email" value={...} onChange={...} />
 * </FormField>
 */
const FormField = ({ label, hint, error, required, children, className = '' }) => {
  const autoId = React.useId();
  const fieldId = children.props.id || autoId;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <label htmlFor={fieldId} className="block text-sm font-medium text-foreground/80">
          {label}
          {required && <span className="text-danger"> *</span>}
        </label>
      )}

      {React.cloneElement(children, {
        id: fieldId,
        error: Boolean(error),
        'aria-describedby': describedBy,
        required,
      })}

      {hint && !error && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
};

FormField.propTypes = {
  label: PropTypes.node,
  hint: PropTypes.node,
  error: PropTypes.node,
  required: PropTypes.bool,
  children: PropTypes.element.isRequired,
  className: PropTypes.string,
};

export default FormField;
